/**
 * Free Maintenance Log Tool
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * Public lead magnet tool - no login required
 * Captures email for marketing follow-up
 * Co-brandable with client logo
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Wrench, Plus, Trash2, Download, Upload, Calendar, DollarSign,
  CheckCircle, Clock, AlertTriangle, Home, Building2, ArrowRight,
  FileText, Mail, Image, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FreeMaintenanceLog = () => {
  const [email, setEmail] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [clientLogo, setClientLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [maintenanceItems, setMaintenanceItems] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    property: '',
    unit: '',
    category: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    cost: '',
    vendor: '',
    date_reported: new Date().toISOString().split('T')[0],
    date_completed: ''
  });

  // Load saved data from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem('freetools_email');
    const savedItems = localStorage.getItem('maintenance_log_items');
    const savedLogo = localStorage.getItem('maintenance_log_logo');
    const savedCompany = localStorage.getItem('maintenance_log_company');
    
    if (savedEmail) {
      setEmail(savedEmail);
      setIsUnlocked(true);
    }
    if (savedItems) {
      setMaintenanceItems(JSON.parse(savedItems));
    }
    if (savedLogo) {
      setClientLogo(savedLogo);
    }
    if (savedCompany) {
      setCompanyName(savedCompany);
    }
  }, []);

  // Save items to localStorage whenever they change
  useEffect(() => {
    if (maintenanceItems.length > 0) {
      localStorage.setItem('maintenance_log_items', JSON.stringify(maintenanceItems));
    }
  }, [maintenanceItems]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      // Save email to backend for marketing
      await fetch(`${BACKEND_URL}/api/free-tools/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          tool: 'maintenance_log',
          source: 'free_tools_page'
        })
      });
    } catch (error) {
      console.log('Lead capture failed, continuing anyway');
    }

    localStorage.setItem('freetools_email', email);
    setIsUnlocked(true);
    toast.success('Welcome! Your free maintenance log is ready.');
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setClientLogo(reader.result);
        localStorage.setItem('maintenance_log_logo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCompanyNameChange = (e) => {
    setCompanyName(e.target.value);
    localStorage.setItem('maintenance_log_company', e.target.value);
  };

  const addMaintenanceItem = () => {
    if (!newItem.property || !newItem.description) {
      toast.error('Please fill in property and description');
      return;
    }

    const item = {
      ...newItem,
      id: Date.now(),
      created_at: new Date().toISOString()
    };

    setMaintenanceItems([item, ...maintenanceItems]);
    setNewItem({
      property: '',
      unit: '',
      category: '',
      description: '',
      priority: 'medium',
      status: 'pending',
      cost: '',
      vendor: '',
      date_reported: new Date().toISOString().split('T')[0],
      date_completed: ''
    });
    setShowAddForm(false);
    toast.success('Maintenance item added!');
  };

  const updateItemStatus = (id, status) => {
    setMaintenanceItems(items =>
      items.map(item =>
        item.id === id
          ? { ...item, status, date_completed: status === 'completed' ? new Date().toISOString().split('T')[0] : '' }
          : item
      )
    );
    toast.success(`Status updated to ${status}`);
  };

  const deleteItem = (id) => {
    setMaintenanceItems(items => items.filter(item => item.id !== id));
    toast.success('Item deleted');
  };

  const exportToCSV = () => {
    const headers = ['Property', 'Unit', 'Category', 'Description', 'Priority', 'Status', 'Cost', 'Vendor', 'Date Reported', 'Date Completed'];
    const rows = maintenanceItems.map(item => [
      item.property,
      item.unit,
      item.category,
      item.description,
      item.priority,
      item.status,
      item.cost,
      item.vendor,
      item.date_reported,
      item.date_completed
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `maintenance_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported to CSV!');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-blue-500" />;
      case 'pending': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const stats = {
    total: maintenanceItems.length,
    pending: maintenanceItems.filter(i => i.status === 'pending').length,
    inProgress: maintenanceItems.filter(i => i.status === 'in_progress').length,
    completed: maintenanceItems.filter(i => i.status === 'completed').length,
    totalCost: maintenanceItems.reduce((sum, i) => sum + (parseFloat(i.cost) || 0), 0)
  };

  // Email capture screen
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Home className="h-8 w-8 text-emerald-600" />
              <span className="text-xl font-bold text-gray-900">Estate Flow</span>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">100% Free Tool</Badge>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 bg-emerald-100 rounded-full mb-4">
              <Wrench className="h-12 w-12 text-emerald-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Free Maintenance Log
            </h1>
            <p className="text-xl text-gray-600 mb-2">
              Track all your property maintenance in one place
            </p>
            <p className="text-gray-500">
              No credit card required • Export anytime • Co-brand with your logo
            </p>
          </div>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle>Get Started Free</CardTitle>
              <CardDescription>
                Enter your email to access your free maintenance log
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Access Free Tool
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-xs text-center text-gray-500">
                  By continuing, you agree to receive occasional updates from Estate Flow.
                  Unsubscribe anytime.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <FileText className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <h3 className="font-semibold">Track Everything</h3>
              <p className="text-sm text-gray-500">Log all maintenance requests and repairs</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <Download className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <h3 className="font-semibold">Export Anytime</h3>
              <p className="text-sm text-gray-500">Download as CSV for your records</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <Image className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <h3 className="font-semibold">Co-Brand</h3>
              <p className="text-sm text-gray-500">Add your company logo</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main tool interface
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with co-branding */}
      <header className="bg-white shadow-sm print:shadow-none">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Client logo */}
              {clientLogo ? (
                <div className="relative group">
                  <img src={clientLogo} alt="Company Logo" className="h-10 max-w-[120px] object-contain" />
                  <button
                    onClick={() => {
                      setClientLogo(null);
                      localStorage.removeItem('maintenance_log_logo');
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-500 transition-colors">
                  <Upload className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Add Your Logo</span>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              )}
              
              <div className="h-8 w-px bg-gray-200" />
              
              {/* Estate Flow branding */}
              <div className="flex items-center gap-2">
                <Home className="h-6 w-6 text-emerald-600" />
                <span className="font-semibold text-gray-700">Estate Flow</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Input
                placeholder="Company Name"
                value={companyName}
                onChange={handleCompanyNameChange}
                className="w-48"
              />
              <Button variant="outline" onClick={exportToCSV} disabled={maintenanceItems.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {companyName ? `${companyName} - ` : ''}Maintenance Log
            </h1>
            <p className="text-gray-500">Track and manage all property maintenance</p>
          </div>
          <Button onClick={() => setShowAddForm(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Maintenance Item
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-gray-500">Total Items</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              <div className="text-sm text-gray-500">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
              <div className="text-sm text-gray-500">In Progress</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              <div className="text-sm text-gray-500">Completed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">${stats.totalCost.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Total Cost</div>
            </CardContent>
          </Card>
        </div>

        {/* Add Form Modal */}
        {showAddForm && (
          <Card className="mb-8 border-2 border-emerald-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Add Maintenance Item
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Property *</Label>
                  <Input
                    placeholder="123 Main Street"
                    value={newItem.property}
                    onChange={(e) => setNewItem({ ...newItem, property: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Unit/Apt</Label>
                  <Input
                    placeholder="Unit 4B"
                    value={newItem.unit}
                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plumbing">Plumbing</SelectItem>
                      <SelectItem value="electrical">Electrical</SelectItem>
                      <SelectItem value="hvac">HVAC</SelectItem>
                      <SelectItem value="appliance">Appliance</SelectItem>
                      <SelectItem value="structural">Structural</SelectItem>
                      <SelectItem value="exterior">Exterior</SelectItem>
                      <SelectItem value="pest">Pest Control</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 lg:col-span-3">
                  <Label>Description *</Label>
                  <Textarea
                    placeholder="Describe the maintenance issue..."
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newItem.priority} onValueChange={(v) => setNewItem({ ...newItem, priority: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estimated Cost</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={newItem.cost}
                      onChange={(e) => setNewItem({ ...newItem, cost: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label>Vendor/Contractor</Label>
                  <Input
                    placeholder="ABC Plumbing"
                    value={newItem.vendor}
                    onChange={(e) => setNewItem({ ...newItem, vendor: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date Reported</Label>
                  <Input
                    type="date"
                    value={newItem.date_reported}
                    onChange={(e) => setNewItem({ ...newItem, date_reported: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button onClick={addMaintenanceItem} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Maintenance Items List */}
        {maintenanceItems.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Wrench className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Maintenance Items Yet</h3>
              <p className="text-gray-500 mb-4">Start tracking your property maintenance by adding your first item.</p>
              <Button onClick={() => setShowAddForm(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-2" />
                Add First Item
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {maintenanceItems.map((item) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusIcon(item.status)}
                        <h3 className="font-semibold">{item.property} {item.unit && `- ${item.unit}`}</h3>
                        <Badge className={getPriorityColor(item.priority)}>
                          {item.priority}
                        </Badge>
                        {item.category && (
                          <Badge variant="outline">{item.category}</Badge>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">{item.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Reported: {item.date_reported}
                        </span>
                        {item.date_completed && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            Completed: {item.date_completed}
                          </span>
                        )}
                        {item.cost && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            ${parseFloat(item.cost).toLocaleString()}
                          </span>
                        )}
                        {item.vendor && (
                          <span>Vendor: {item.vendor}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Select value={item.status} onValueChange={(v) => updateItemStatus(item.id, v)}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upgrade CTA */}
        <Card className="mt-12 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <CardContent className="py-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Ready for More?</h3>
                <p className="text-emerald-100 mb-4">
                  Upgrade to Estate Flow for automated vendor dispatch, payment tracking, 
                  tenant communication, and rent collection - all in one platform.
                </p>
                <ul className="text-sm text-emerald-100 space-y-1">
                  <li>✓ Automated maintenance workflows</li>
                  <li>✓ Vendor payment management</li>
                  <li>✓ Tenant portal for requests</li>
                  <li>✓ Rent collection & accounting</li>
                </ul>
              </div>
              <Button 
                size="lg" 
                className="bg-white text-emerald-600 hover:bg-emerald-50"
                onClick={() => window.location.href = '/onboarding'}
              >
                Upgrade to Estate Flow
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-gray-500">
          <p>Free Maintenance Log by <a href="/" className="text-emerald-600 hover:underline">Estate Flow</a></p>
          <p className="mt-1">© 2025 Sebastian Alliance Group LLC. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
};

export default FreeMaintenanceLog;
